import { NewOrderItem } from 'types';
import type { ShipmentCarrierResponse } from 'services/openapi/apiSchemas';
import { getShipmentInfo } from 'pages/Shipping/utils';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import { NavigationContext } from 'context/NavigationContext';
import { useContext } from 'react';

type ShipmentCarrier = ShipmentCarrierResponse;

export const SKIP_SHOWING_DETAILS_LINK_FAKE_ASSET_ID =
  'SKIP_SHOWING_DETAILS_LINK_FAKE_ASSET_ID';
export const getOrderItemSource = (orderItem: NewOrderItem) => {
  return orderItem.asset ? 'Inventory' : 'Marketplace';
};

/**
 * Gets the carrier name from a carrier ID using the carriers list
 * @param carrierId - The ID of the carrier to look up
 * @param carriers - List of available carriers
 * @returns The carrier name or 'Not available' if not found
 */
export const getCarrierName = (
  carrierId: string | null | undefined,
  carriers?: ShipmentCarrier[]
): string => {
  if (!carrierId || !carriers) return '-';
  const carrier = carriers.find((c) => c.id === carrierId);
  return carrier?.name?.toUpperCase() ?? '-';
};

/**
 * Sorts order items by expected delivery date
 * Items with delivery dates come first, sorted from earliest to latest
 * Items without delivery dates maintain their original order
 * @param orderItems - Array of order items to sort
 * @returns Sorted array of order items
 */
export const sortOrderItemsByDeliveryDate = (
  orderItems: NewOrderItem[]
): NewOrderItem[] => {
  return [...orderItems].sort((a, b) => {
    const shipmentInfoA = getShipmentInfo(a);
    const shipmentInfoB = getShipmentInfo(b);

    // If both items have delivery dates, compare them
    if (
      shipmentInfoA.expectedDeliveryDate &&
      shipmentInfoB.expectedDeliveryDate
    ) {
      return (
        new Date(shipmentInfoA.expectedDeliveryDate).getTime() -
        new Date(shipmentInfoB.expectedDeliveryDate).getTime()
      );
    }

    // If only one has a delivery date, prioritize it
    if (shipmentInfoA.expectedDeliveryDate) return -1;
    if (shipmentInfoB.expectedDeliveryDate) return 1;

    // If neither has a delivery date, maintain original order
    return 0;
  });
};

/**
 * Sorts order items by order item number
 * @param orderItems - Array of order items to sort
 * @returns Sorted array of order items
 */
export const sortOrderItemsByNumber = (
  orderItems: NewOrderItem[]
): NewOrderItem[] => {
  return [...orderItems].sort((a, b) => {
    // If either item doesn't have a number, move it to the end
    if (!a.orderItemNumber) return 1;
    if (!b.orderItemNumber) return -1;
    return a.orderItemNumber.localeCompare(b.orderItemNumber);
  });
};

export const getAssetLink = (orderItem: NewOrderItem, isRetrieval: boolean) => {
  const assetId = orderItem?.asset?.id;
  const assetNumber = orderItem?.asset?.assetNumber;
  const isOutboundOrderItem = orderItem.inboundOrderItemId && !isRetrieval;
  const hasFakeAbsenteeAssetId = assetId?.includes(
    SKIP_SHOWING_DETAILS_LINK_FAKE_ASSET_ID
  );
  const navigator = useContext(NavigationContext);

  if (
    hasFakeAbsenteeAssetId ||
    !assetNumber ||
    isOutboundOrderItem ||
    !assetId
  ) {
    return '-';
  }

  return (
    <Box>
      <Link
        href={navigator.constructPathToAssetDetails(assetId)}
        target="_blank"
        underline="always"
        color="colors.woodSmoke"
      >
        {assetNumber}
      </Link>
    </Box>
  );
};
